import { FC, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { getCanvasAnimationLayerSx } from "./canvasAnimationLayerSx";
import type { CanvasAnimationLayoutVariant, CanvasBackgroundTheme } from "./canvasBackgroundTheme";
import { resizeCanvasKeepingPositions } from "./resizeCanvasKeepingPositions";
import { resolveCanvasSurfaceSize } from "./resolveCanvasSurfaceSize";
import { subscribeVisibilityAwareAnimationLoop } from "./subscribeVisibilityAwareAnimationLoop";

const CARDS_COUNT = 12;
const DICE_COUNT = 10;
const MOBILE_ITEMS_RATIO = 0.5;

export type CardsDiceAnimationProps = {
  hue: number;
  theme: CanvasBackgroundTheme;
  variant?: CanvasAnimationLayoutVariant;
  isMobile?: boolean;
  frameStride?: number;
};

const SUITS = ["♠", "♥", "♦", "♣"] as const;

const DOT_OFFSETS: Record<1 | 2 | 3 | 4 | 5 | 6, [number, number][]> = {
  1: [[0, 0]],
  2: [
    [-1, -1],
    [1, 1],
  ],
  3: [
    [-1, -1],
    [0, 0],
    [1, 1],
  ],
  4: [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
  ],
  5: [
    [-1, -1],
    [1, -1],
    [0, 0],
    [-1, 1],
    [1, 1],
  ],
  6: [
    [-1, -1],
    [1, -1],
    [-1, 0],
    [1, 0],
    [-1, 1],
    [1, 1],
  ],
};

type CardItem = {
  kind: "card";
  x: number;
  y: number;
  rot: number;
  rotSpeed: number;
  vx: number;
  vy: number;
  size: number;
  suit: (typeof SUITS)[number];
  opacity: number;
};

type DieItem = {
  kind: "die";
  x: number;
  y: number;
  rot: number;
  rotSpeed: number;
  vx: number;
  vy: number;
  size: number;
  face: 1 | 2 | 3 | 4 | 5 | 6;
  opacity: number;
};

type DriftItem = CardItem | DieItem;

/** Playing cards + dice silhouettes — Delightful-er Forms spec. */
export const CardsDiceAnimation: FC<CardsDiceAnimationProps> = ({
  hue,
  theme,
  variant = "viewportFixed",
  isMobile = false,
  frameStride = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasMaybe = canvasRef.current;
    if (!canvasMaybe) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const contextMaybe = canvasMaybe.getContext("2d");
    if (!contextMaybe) {
      return;
    }

    const canvasEl: HTMLCanvasElement = canvasMaybe;
    const context: CanvasRenderingContext2D = contextMaybe;

    function tc(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 68%, 78%, ${alpha * 0.72})`;
        case "light":
          return `hsla(${hue}, 48%, 52%, ${alpha * 0.65})`;
        case "darkdull":
          return `hsla(${hue}, 22%, 68%, ${alpha * 0.52})`;
        case "lightdull":
          return `hsla(${hue}, 12%, 52%, ${alpha * 0.5})`;
        default:
          return `hsla(${hue}, 68%, 78%, ${alpha * 0.72})`;
      }
    }

    function resizeCanvas(): void {
      const { width, height } = resolveCanvasSurfaceSize(canvasEl);
      canvasEl.width = width;
      canvasEl.height = height;
    }

    resizeCanvas();

    function rrect(x: number, y: number, width: number, height: number, radius: number): void {
      context.beginPath();
      context.moveTo(x + radius, y);
      context.lineTo(x + width - radius, y);
      context.arcTo(x + width, y, x + width, y + radius, radius);
      context.lineTo(x + width, y + height - radius);
      context.arcTo(x + width, y + height, x + width - radius, y + height, radius);
      context.lineTo(x + radius, y + height);
      context.arcTo(x, y + height, x, y + height - radius, radius);
      context.lineTo(x, y + radius);
      context.arcTo(x, y, x + radius, y, radius);
      context.closePath();
    }

    function mkCard(): CardItem {
      return {
        kind: "card",
        x: Math.random() * canvasEl.width,
        y: Math.random() * canvasEl.height,
        rot: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.007,
        vx: (Math.random() - 0.5) * 0.28,
        vy: (Math.random() - 0.5) * 0.28,
        size: Math.random() * 16 + 20,
        suit: SUITS[Math.floor(Math.random() * SUITS.length)]!,
        opacity: Math.random() * 0.38 + 0.3,
      };
    }

    function mkDie(): DieItem {
      return {
        kind: "die",
        x: Math.random() * canvasEl.width,
        y: Math.random() * canvasEl.height,
        rot: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.01,
        vx: (Math.random() - 0.5) * 0.32,
        vy: (Math.random() - 0.5) * 0.32,
        size: Math.random() * 10 + 18,
        face: (Math.floor(Math.random() * 6) + 1) as 1 | 2 | 3 | 4 | 5 | 6,
        opacity: Math.random() * 0.38 + 0.3,
      };
    }

    const cardCount = isMobile ? Math.round(CARDS_COUNT * MOBILE_ITEMS_RATIO) : CARDS_COUNT;
    const diceCount = isMobile ? Math.round(DICE_COUNT * MOBILE_ITEMS_RATIO) : DICE_COUNT;
    const items: DriftItem[] = [
      ...Array.from({ length: cardCount }, () => mkCard()),
      ...Array.from({ length: diceCount }, () => mkDie()),
    ];

    function onResize(): void {
      resizeCanvasKeepingPositions(canvasEl, items);
    }

    window.addEventListener("resize", onResize);

    const stopAnimationLoop = subscribeVisibilityAwareAnimationLoop({ frameStride }, (frameSteps) => {
      context.clearRect(0, 0, canvasEl.width, canvasEl.height);

      for (const item of items) {
        context.save();
        context.translate(item.x, item.y);
        context.rotate(item.rot);

        const strokeColor = tc(item.opacity);
        const fillColor = tc(item.opacity * 0.15);
        const accentColor = tc(item.opacity * 0.9);

        if (item.kind === "card") {
          const width = item.size;
          const height = width * 1.42;
          const cornerRadius = width * 0.1;

          rrect(-width / 2, -height / 2, width, height, cornerRadius);
          context.fillStyle = fillColor;
          context.fill();
          context.strokeStyle = strokeColor;
          context.lineWidth = 1.5;
          context.stroke();

          context.font = `${item.size * 0.6}px serif`;
          context.fillStyle = accentColor;
          context.textAlign = "center";
          context.textBaseline = "middle";
          context.fillText(item.suit, 0, 0);
        } else {
          const side = item.size;
          const cornerRadius = side * 0.14;

          rrect(-side / 2, -side / 2, side, side, cornerRadius);
          context.fillStyle = fillColor;
          context.fill();
          context.strokeStyle = strokeColor;
          context.lineWidth = 1.5;
          context.stroke();

          const dotRadius = side * 0.085;
          const offset = side * 0.27;
          context.fillStyle = accentColor;
          for (const [dotDx, dotDy] of DOT_OFFSETS[item.face]) {
            context.beginPath();
            context.arc(dotDx * offset, dotDy * offset, dotRadius, 0, Math.PI * 2);
            context.fill();
          }
        }

        context.restore();

        item.x += item.vx * frameSteps;
        item.y += item.vy * frameSteps;
        item.rot += item.rotSpeed * frameSteps;

        const pad = 60;
        if (item.x < -pad) {
          item.x = canvasEl.width + pad;
        }
        if (item.x > canvasEl.width + pad) {
          item.x = -pad;
        }
        if (item.y < -pad) {
          item.y = canvasEl.height + pad;
        }
        if (item.y > canvasEl.height + pad) {
          item.y = -pad;
        }
      }
    });

    return () => {
      stopAnimationLoop();
      window.removeEventListener("resize", onResize);
    };
  }, [hue, theme, isMobile, frameStride]);

  return <Box component="canvas" ref={canvasRef} sx={getCanvasAnimationLayerSx(variant)} />;
};
